import {
  Button,
  chakra,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  HStack,
  IconButton,
  Input,
  Select,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { FormInput, FormTextArea, FormSwitch } from "../../../../components/Forms";
import { MultiSelect } from "../../../../components/MultiSelect";
import EsgFormLabel from "../../../../components/Forms/EsgFormLabel";
import { useGetAllOrganizations } from "../../../../hooks/query/esg-admin/esg-admin";

/**
 * Rule dimension options for the Prompt Manager admin form.
 *
 * These define which values admins can select when configuring display rules.
 * Values must match what the consuming pages pass via useContextualPrompts().
 *
 * To add a new dimension:
 *   1. Add a new *_OPTIONS constant below
 *   2. Add default value (empty array) in defaultValues
 *   3. Add a Controller + MultiSelect in the appropriate section (General or Question-related)
 *   4. Map it in prepareSubmitData -> rules object
 *   5. Update useContextualPrompts.js to accept the new filter
 *   6. See PromptRules.java (backend) for the backend checklist
 */

// --- General rules ---

const LOCATION_OPTIONS = [
  { label: "Disclosure", value: "disclosure" },
  { label: "AI Draft", value: "ai-draft" },
  { label: "Chat", value: "chat" },
  { label: "Report Doc Chat", value: "report-doc-chat" },
  { label: "Report Doc Template", value: "report-doc-template" },
  { label: "AI Review Dashboard", value: "ai-review-dashboard" },
];


// --- Question-related rules ---

const STAGE_OPTIONS = [
  { label: "Start", value: "START" },
  { label: "AI Queue", value: "AI_QUEUE" },
  { label: "Expert Review", value: "EXPERT_REVIEW" },
  { label: "Pending", value: "PENDING" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Internal Review", value: "INTERNAL_REVIEW" },
  { label: "Audit", value: "AUDIT" },
  { label: "Complete", value: "COMPLETE" },
];

const COMPLETENESS_OPTIONS = [
  { label: "None — No answer", value: "NONE" },
  { label: "Low — Requires further data", value: "LOW" },
  { label: "Medium — Requires iteration", value: "MEDIUM" },
  { label: "High — Done", value: "HIGH" },
];

const CONFIDENCE_OPTIONS = [
  { label: "None", value: "NONE" },
  { label: "Low", value: "LOW" },
  { label: "Medium — Limited", value: "MEDIUM" },
  { label: "High — Reasonable", value: "HIGH" },
];

function mapArrayToOptions(arr, options) {
  if (!arr || !Array.isArray(arr) || arr.length === 0) return [];
  return arr.map((v) => options.find((o) => o.value === v)).filter(Boolean);
}

function PageTagsInput({ value = [], onChange }) {
  const [inputValue, setInputValue] = useState("");

  const addPage = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue("");
  };

  const removePage = (page) => {
    onChange(value.filter((p) => p !== page));
  };

  return (
    <Flex direction="column" gap={2}>
      <HStack>
        <Input
          size="sm"
          placeholder="e.g. issb/dashboard"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addPage();
            }
          }}
        />
        <Button size="sm" onClick={addPage} isDisabled={!inputValue.trim()}>
          Add
        </Button>
      </HStack>
      {value.length > 0 && (
        <Wrap>
          {value.map((page) => (
            <WrapItem key={page}>
              <Tag size="md" colorScheme="blue" borderRadius="full">
                <TagLabel>{page}</TagLabel>
                <TagCloseButton onClick={() => removePage(page)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}
    </Flex>
  );
}

function PromptModal({ prompt, isOpen, onClose, onSubmit, onDelete, onDuplicate, isSubmitting }) {
  const { data: organizations = [], isLoading: isLoadingOrgs } = useGetAllOrganizations({
    enabled: isOpen,
  });

  const orgOptions = useMemo(() => {
    return organizations.map((org) => ({
      label: org.name,
      value: org.tenantId,
    }));
  }, [organizations]);

  const defaultValues = useMemo(() => {
    if (prompt) {
      return {
        name: prompt.name || "",
        description: prompt.description || "",
        content: prompt.content || "",
        isUniversal: prompt.isUniversal || false,
        organizations: prompt.organizationTenantIds?.map((tenantId) => {
          const org = organizations.find((o) => o.tenantId === tenantId);
          return {
            label: org?.name || tenantId,
            value: tenantId,
          };
        }) || [],
        ruleLocations: mapArrayToOptions(prompt.rules?.locations, LOCATION_OPTIONS),
        rulePages: prompt.rules?.pages || [],
        ruleStages: mapArrayToOptions(prompt.rules?.stages, STAGE_OPTIONS),
        ruleCompleteness: mapArrayToOptions(prompt.rules?.completeness, COMPLETENESS_OPTIONS),
        ruleConfidence: mapArrayToOptions(prompt.rules?.confidence, CONFIDENCE_OPTIONS),
        chatShowWhen: prompt.rules?.chatShowWhen || "both",
      };
    }
    return {
      name: "",
      description: "",
      content: "",
      isUniversal: true,
      organizations: [],
      ruleLocations: [],
      rulePages: [],
      ruleStages: [],
      ruleCompleteness: [],
      ruleConfidence: [],
      chatShowWhen: "both",
    };
  }, [prompt, organizations]);

  const {
    register,
    trigger,
    getValues,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [reset, defaultValues, isOpen]);

  const isUniversal = watch("isUniversal");
  const ruleLocations = watch("ruleLocations") || [];
  const selectedLocationValues = ruleLocations.map((o) => o.value);
  const showChatOptions = selectedLocationValues.some((v) => ["report-doc-chat", "chat"].includes(v));
  const showQuestionRules = selectedLocationValues.some((v) => ["disclosure", "ai-draft"].includes(v));

  const onFormSubmit = async (e) => {
    e.preventDefault();
    // Use trigger() + getValues() instead of handleSubmit() to avoid
    // react-hook-form's deep clone, which overflows on React elements
    // stored by chakra-react-select in MultiSelect form fields.
    const isValid = await trigger();
    if (!isValid) return;
    const data = getValues();
    const locations = data.ruleLocations?.map((o) => o.value) || [];
    const pages = data.rulePages || [];
    const stages = data.ruleStages?.map((o) => o.value) || [];
    const completeness = data.ruleCompleteness?.map((o) => o.value) || [];
    const confidence = data.ruleConfidence?.map((o) => o.value) || [];

    const mappedData = {
      name: data.name,
      description: data.description || null,
      content: data.content,
      isUniversal: data.isUniversal,
      organizationTenantIds: data.isUniversal ? [] : data.organizations?.map((org) => org.value) || [],
      rules: { locations, pages, stages, completeness, confidence, chatShowWhen: data.chatShowWhen || "both" },
    };
    onSubmit(prompt?.id, mappedData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="pureWhite">
        <chakra.form onSubmit={onFormSubmit}>
          <ModalHeader>{prompt ? "Edit Prompt" : "Create Prompt"}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Flex direction="column" gap="4" marginBottom={4}>
              <FormInput
                label="Name *"
                placeholder="Enter prompt name"
                maxInlineSize="var(--chakra-sizes-full)"
                {...register("name", {
                  required: "Name is required",
                })}
                errors={errors}
              />

              <FormTextArea
                label="Description"
                placeholder="Enter a brief description"
                {...register("description")}
                errors={errors}
              />

              <FormTextArea
                label="Content *"
                placeholder="Enter the prompt content"
                {...register("content", {
                  required: "Content is required",
                })}
                errors={errors}
                blockSize="48"
              />

              <FormSwitch
                label="Universal (available to all organizations)"
                name="isUniversal"
                control={control}
                errors={errors}
              />

              {!isUniversal && (
                <>
                  <EsgFormLabel>Organizations</EsgFormLabel>
                  <Controller
                    control={control}
                    name="organizations"
                    rules={{
                      validate: (value) => {
                        if (!isUniversal && (!value || value.length === 0)) {
                          return "Select at least one organization when not universal";
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <MultiSelect
                        options={orgOptions}
                        placeholder="Select organizations"
                        value={field.value}
                        onChange={field.onChange}
                        isMulti
                        isLoading={isLoadingOrgs}
                      />
                    )}
                  />
                  {errors.organizations && (
                    <chakra.span color="red.500" fontSize="sm">
                      {errors.organizations.message}
                    </chakra.span>
                  )}
                </>
              )}

              {/* --- General Rules --- */}
              <Divider my={2} />
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                General Rules
              </Text>

              <EsgFormLabel>Location *</EsgFormLabel>
              <Controller
                control={control}
                name="ruleLocations"
                rules={{
                  validate: (value) => {
                    if (!value || value.length === 0) {
                      return "Select at least one location";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <MultiSelect
                    options={LOCATION_OPTIONS}
                    placeholder="Select locations"
                    value={field.value}
                    onChange={field.onChange}
                    isMulti
                  />
                )}
              />
              {errors.ruleLocations && (
                <chakra.span color="red.500" fontSize="sm">
                  {errors.ruleLocations.message}
                </chakra.span>
              )}

              <EsgFormLabel>Page (empty = all pages)</EsgFormLabel>
              <Controller
                control={control}
                name="rulePages"
                render={({ field }) => (
                  <PageTagsInput value={field.value} onChange={field.onChange} />
                )}
              />

              {/* --- Chat behaviour --- */}
              {showChatOptions && (
                <>
                  <EsgFormLabel>Show When</EsgFormLabel>
                  <Controller
                    control={control}
                    name="chatShowWhen"
                    render={({ field }) => (
                      <Select {...field}>
                        <option value="both">Always (fresh & in-conversation)</option>
                        <option value="initial">Fresh conversation only</option>
                        <option value="in-conversation">In-conversation only</option>
                      </Select>
                    )}
                  />
                </>
              )}

              {/* --- Question-related Rules --- */}
              {showQuestionRules && (
                <>
                  <Divider my={2} />
                  <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                    Question Rules (empty = all)
                  </Text>

                  <EsgFormLabel>Stage</EsgFormLabel>
                  <Controller
                    control={control}
                    name="ruleStages"
                    render={({ field }) => (
                      <MultiSelect
                        options={STAGE_OPTIONS}
                        placeholder="All stages"
                        value={field.value}
                        onChange={field.onChange}
                        isMulti
                      />
                    )}
                  />

                  <EsgFormLabel>Completeness</EsgFormLabel>
                  <Controller
                    control={control}
                    name="ruleCompleteness"
                    render={({ field }) => (
                      <MultiSelect
                        options={COMPLETENESS_OPTIONS}
                        placeholder="All levels"
                        value={field.value}
                        onChange={field.onChange}
                        isMulti
                      />
                    )}
                  />

                  <EsgFormLabel>Confidence</EsgFormLabel>
                  <Controller
                    control={control}
                    name="ruleConfidence"
                    render={({ field }) => (
                      <MultiSelect
                        options={CONFIDENCE_OPTIONS}
                        placeholder="All levels"
                        value={field.value}
                        onChange={field.onChange}
                        isMulti
                      />
                    )}
                  />
                </>
              )}
            </Flex>
          </ModalBody>

          <ModalFooter justifyContent={prompt ? "space-between" : "flex-end"}>
            {prompt && (
              <HStack>
                {onDelete && (
                  <Button variant="ghost" colorScheme="red" onClick={onDelete}>
                    Delete
                  </Button>
                )}
                {onDuplicate && (
                  <Button variant="ghost" onClick={onDuplicate}>
                    Duplicate
                  </Button>
                )}
              </HStack>
            )}
            <HStack>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="customScheme" type="submit" isLoading={isSubmitting}>
                {prompt ? "Save Changes" : "Create"}
              </Button>
            </HStack>
          </ModalFooter>
        </chakra.form>
      </ModalContent>
    </Modal>
  );
}

export default PromptModal;
